// src/components/document/DocumentGraphView.tsx
// Knowledge graph visualization for zettelkasten-style document relationships.
// Uses force-directed layout computed in pure JS (no external graph library).

import { useEffect, useRef, useState, useMemo } from 'react';
import { useI18n } from '@react/hooks';
import {
  getStatusMapping,
  documentStatusMap,
} from '@/features/document/logic';
import type { DocumentGraph, GraphNode } from '@/features/document/types';

// ---------------------------------------------------------------------------
// Layout — simple force-directed simulation
// ---------------------------------------------------------------------------

interface LayoutNode extends GraphNode {
  x: number;
  y: number;
  vx: number;
  vy: number;
}

function computeLayout(
  graph: DocumentGraph,
  width: number,
  height: number,
  iterations = 80,
): LayoutNode[] {
  const cx = width / 2;
  const cy = height / 2;

  const layoutNodes: LayoutNode[] = graph.nodes.map((n, i) => ({
    ...n,
    x: cx + (Math.cos((i / graph.nodes.length) * Math.PI * 2) * Math.min(width, height) * 0.3),
    y: cy + (Math.sin((i / graph.nodes.length) * Math.PI * 2) * Math.min(width, height) * 0.3),
    vx: 0,
    vy: 0,
  }));

  if (layoutNodes.length === 0) return layoutNodes;

  const nodeMap = new Map(layoutNodes.map((n) => [n.id, n]));
  const repulsionStrength = 2000;
  const attractionStrength = 0.02;
  const damping = 0.85;
  const idealDistance = 120;

  for (let iter = 0; iter < iterations; iter++) {
    // Repulsion between all pairs
    for (let i = 0; i < layoutNodes.length; i++) {
      for (let j = i + 1; j < layoutNodes.length; j++) {
        const a = layoutNodes[i];
        const b = layoutNodes[j];
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const dist = Math.max(Math.sqrt(dx * dx + dy * dy), 1);
        const force = repulsionStrength / (dist * dist);
        const fx = (dx / dist) * force;
        const fy = (dy / dist) * force;
        a.vx -= fx;
        a.vy -= fy;
        b.vx += fx;
        b.vy += fy;
      }
    }

    // Attraction along edges
    for (const edge of graph.edges) {
      const a = nodeMap.get(edge.sourceId);
      const b = nodeMap.get(edge.targetId);
      if (!a || !b) continue;
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const force = (dist - idealDistance) * attractionStrength;
      const fx = (dx / Math.max(dist, 1)) * force;
      const fy = (dy / Math.max(dist, 1)) * force;
      a.vx += fx;
      a.vy += fy;
      b.vx -= fx;
      b.vy -= fy;
    }

    // Center gravity
    for (const n of layoutNodes) {
      n.vx += (cx - n.x) * 0.001;
      n.vy += (cy - n.y) * 0.001;
    }

    // Apply velocities with damping
    for (const n of layoutNodes) {
      n.vx *= damping;
      n.vy *= damping;
      n.x += n.vx;
      n.y += n.vy;
      // Keep in bounds
      n.x = Math.max(40, Math.min(width - 40, n.x));
      n.y = Math.max(40, Math.min(height - 40, n.y));
    }
  }

  return layoutNodes;
}

// ---------------------------------------------------------------------------
// Edge color by relation type
// ---------------------------------------------------------------------------

const EDGE_COLORS: Record<string, string> = {
  wikilink: 'hsl(var(--primary))',
  parent: 'hsl(var(--muted-foreground))',
  resource: 'hsl(var(--accent-foreground))',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface DocumentGraphViewProps {
  graph: DocumentGraph;
  onSelectNode: (id: string) => void;
  onOpenNode: (id: string) => void;
  selectedNodeId?: string;
}

export function DocumentGraphView({
  graph,
  onSelectNode,
  onOpenNode,
  selectedNodeId,
}: DocumentGraphViewProps) {
  const { t } = useI18n();
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 500 });
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);

  // Measure container
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setDimensions({ width: Math.max(400, width), height: Math.max(300, height) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const layoutNodes = useMemo(
    () => computeLayout(graph, dimensions.width, dimensions.height),
    [graph, dimensions.width, dimensions.height],
  );

  const nodeMap = useMemo(
    () => new Map(layoutNodes.map((n) => [n.id, n])),
    [layoutNodes],
  );

  // Empty state
  if (graph.nodes.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <svg className="w-12 h-12 text-muted-foreground/50 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
          <circle cx="6" cy="6" r="3" /><circle cx="18" cy="6" r="3" /><circle cx="12" cy="18" r="3" />
          <path d="M8.5 7.5l3 7M15.5 7.5l-3 7M7.5 5h9" />
        </svg>
        <p className="text-sm text-muted-foreground">{t('document.graph.empty')}</p>
        <p className="text-xs text-muted-foreground/70 mt-1">{t('document.graph.emptyHint')}</p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="relative w-full rounded-lg border border-border bg-background overflow-hidden"
      style={{ minHeight: 400 }}
    >
      <svg
        width={dimensions.width}
        height={dimensions.height}
        className="block"
      >
        {/* Edges */}
        {graph.edges.map((edge, i) => {
          const source = nodeMap.get(edge.sourceId);
          const target = nodeMap.get(edge.targetId);
          if (!source || !target) return null;
          const isHighlighted = hoveredNode === edge.sourceId || hoveredNode === edge.targetId;
          return (
            <line
              key={i}
              x1={source.x}
              y1={source.y}
              x2={target.x}
              y2={target.y}
              stroke={EDGE_COLORS[edge.relation] || 'hsl(var(--border))'}
              strokeWidth={isHighlighted ? 2 : 1}
              strokeOpacity={isHighlighted ? 0.8 : 0.3}
              strokeDasharray={edge.relation === 'parent' ? '4,4' : undefined}
            />
          );
        })}

        {/* Nodes */}
        {layoutNodes.map((node) => {
          const isSelected = node.id === selectedNodeId;
          const isHovered = node.id === hoveredNode;
          const radius = Math.max(6, Math.min(18, 6 + node.connections * 2));
          const statusInfo = getStatusMapping(documentStatusMap, node.status);

          // Node color based on status variant
          const colorMap: Record<string, string> = {
            success: 'hsl(var(--primary))',
            warning: 'hsl(142 71% 45%)',
            error: 'hsl(0 84% 60%)',
            primary: 'hsl(var(--primary))',
            secondary: 'hsl(var(--muted-foreground))',
            default: 'hsl(var(--foreground))',
          };
          const fill = colorMap[statusInfo.variant] || 'hsl(var(--primary))';

          return (
            <g
              key={node.id}
              className="cursor-pointer"
              onClick={() => onSelectNode(node.id)}
              onDoubleClick={() => onOpenNode(node.id)}
              onMouseEnter={() => setHoveredNode(node.id)}
              onMouseLeave={() => setHoveredNode(null)}
            >
              {/* Focus ring */}
              {(isSelected || node.isFocus) && (
                <circle
                  cx={node.x}
                  cy={node.y}
                  r={radius + 4}
                  fill="none"
                  stroke="hsl(var(--ring))"
                  strokeWidth={2}
                  strokeDasharray={node.isFocus ? undefined : '3,3'}
                />
              )}
              {/* Node circle */}
              <circle
                cx={node.x}
                cy={node.y}
                r={radius}
                fill={fill}
                fillOpacity={isSelected || isHovered ? 1 : 0.7}
                stroke="hsl(var(--background))"
                strokeWidth={2}
              />
              {/* Label */}
              {(isHovered || isSelected || node.connections >= 3) && (
                <text
                  x={node.x}
                  y={node.y + radius + 14}
                  textAnchor="middle"
                  className="text-[11px] fill-foreground pointer-events-none"
                  style={{ fontFamily: 'inherit' }}
                >
                  {node.title.length > 24 ? node.title.slice(0, 22) + '...' : node.title}
                </text>
              )}
            </g>
          );
        })}
      </svg>

      {/* Legend */}
      <div className="absolute bottom-3 left-3 flex items-center gap-4 text-[10px] text-muted-foreground bg-background/80 backdrop-blur-sm rounded-md px-2 py-1 border border-border">
        <span className="flex items-center gap-1">
          <span className="w-6 h-px bg-primary inline-block" />
          {t('document.graph.wikilink')}
        </span>
        <span className="flex items-center gap-1">
          <span className="w-6 h-px bg-muted-foreground inline-block" style={{ borderTop: '1px dashed' }} />
          {t('document.graph.parent')}
        </span>
      </div>

      {/* Stats */}
      <div className="absolute top-3 right-3 text-[10px] text-muted-foreground bg-background/80 backdrop-blur-sm rounded-md px-2 py-1 border border-border">
        {t('document.graph.stats', { nodes: graph.nodes.length, edges: graph.edges.length })}
      </div>
    </div>
  );
}
